import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../components/Button";
import { Body, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import type { PaymentMethodResponse } from "../../lib/api";
import styles from "./Shopping.module.css";

// The deadlock fix (operator decision 2026-08-28): a store with no vaulted card can save
// one right here — the same SetupIntent vault flow as the wizard's billing step, minus the
// activation. The first vaulted card becomes the store's primary payment method; the
// wizard's billing step later reuses it, and the subscription stays pending_billing.
type Phase =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "unconfigured" }
  | { kind: "form"; publishableKey: string; clientSecret: string };

type StoreCardCaptureProps = {
  storeId: string;
  onVaulted: (method: PaymentMethodResponse) => void;
};

export const StoreCardCapture = ({ storeId, onVaulted }: StoreCardCaptureProps) => {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  const load = useCallback(async (): Promise<void> => {
    setPhase({ kind: "loading" });
    try {
      const config = await api.getBillingConfig();
      if (!config.publishable_key) {
        setPhase({ kind: "unconfigured" });
        return;
      }
      const intent = await api.createBillingSetupIntent(storeId);
      setPhase({
        kind: "form",
        publishableKey: config.publishable_key,
        clientSecret: intent.client_secret,
      });
    } catch (e) {
      setPhase({
        kind: "error",
        message: e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't load billing.",
      });
    }
  }, [storeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const publishableKey = phase.kind === "form" ? phase.publishableKey : null;
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  if (phase.kind === "loading") {
    return (
      <section className={styles.cardCapture} aria-label="Add a payment card">
        <Body>
          <Muted>Loading billing…</Muted>
        </Body>
      </section>
    );
  }

  if (phase.kind === "unconfigured") {
    return (
      <div className={styles.banner}>
        <div className={styles.bannerTitle}>Billing isn't configured yet</div>
        <div className={styles.bannerBody}>
          This deployment has no payment processor set up, so device orders aren't available right
          now.
        </div>
      </div>
    );
  }

  if (phase.kind === "error") {
    return (
      <div className={styles.banner} role="alert">
        <div className={styles.bannerTitle}>Couldn't load billing</div>
        <div className={styles.bannerBody}>{phase.message}</div>
        <div>
          <Button type="button" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <section className={styles.cardCapture} aria-label="Add a payment card">
      <Elements stripe={stripePromise} options={{ clientSecret: phase.clientSecret }}>
        <CardForm storeId={storeId} onVaulted={onVaulted} />
      </Elements>
    </section>
  );
};

const CardForm = ({ storeId, onVaulted }: StoreCardCaptureProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (saving || !stripe || !elements) return;
    setError(null);
    setSaving(true);
    try {
      const result = await stripe.confirmSetup({ elements, redirect: "if_required" });
      if (result.error) {
        setError(result.error.message ?? "Your card couldn't be confirmed. Try again.");
        return;
      }
      const pm = result.setupIntent.payment_method;
      const pmId = typeof pm === "string" ? pm : (pm?.id ?? null);
      if (pmId === null) {
        setError("Card confirmation didn't return a payment method. Try again.");
        return;
      }
      onVaulted(await api.addPaymentMethod(storeId, { stripe_pm_id: pmId }));
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't save the card.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.cardCaptureTitle}>Add a payment card</div>
      <Body>
        <Muted>
          Add a payment card for this store — it will also be used for your subscription when you
          finish setup. Saving a card doesn't start a subscription; only this order is charged
          today.
        </Muted>
      </Body>
      {error && (
        <div className={styles.banner} role="alert">
          <div className={styles.bannerTitle}>Couldn't save the card</div>
          <div className={styles.bannerBody}>{error}</div>
        </div>
      )}
      <PaymentElement />
      <div className={styles.actions}>
        <Button type="submit" disabled={saving || !stripe || !elements}>
          {saving ? "Saving…" : "Save card"}
        </Button>
      </div>
    </form>
  );
};
